import {
  ChromeExtensionMessageType,
  ExtensionMessage,
} from 'clerk_common/types';
import { createContext, useEffect, useState } from 'react';

export type FetchedDomContent = {
  domContent: string;
  timeReceived: Date;
};

export type RefetchDomOptions = {
  approvedHostname?: string;
  url?: string;
  useNoModify?: boolean;
};

export type RefetchDomFn = (
  timeoutMs: number,
  options?: RefetchDomOptions
) => Promise<FetchedDomContent | null>;

export type RefetchDatEquipmentExpandedDomFn = (
  timeoutMs: number
) => Promise<FetchedDomContent>;

type ExtensionMessagingContextType = {
  sendMessageToParent: (data: ExtensionMessage) => void;
  dom: FetchedDomContent | null;
  domIframe: FetchedDomContent | null;
  parentUrl: string;
  refetchDom: RefetchDomFn;
  refetchDatEquipmentExpandedDom: RefetchDatEquipmentExpandedDomFn;
};

export const ExtensionMessagingContext =
  createContext<ExtensionMessagingContextType>({
    sendMessageToParent: () => null,
    dom: null,
    domIframe: null,
    refetchDom: () => Promise.resolve(null),
    refetchDatEquipmentExpandedDom: () =>
      Promise.resolve({ domContent: '', timeReceived: new Date() }),
    parentUrl: '',
  });

export const ExtensionMessagingContextProvider = ({
  children,
  setParentUrl,
  parentUrl,
}: {
  children: JSX.Element;
  setParentUrl: (url: string) => void;
  parentUrl: string;
}) => {
  const [dom, setDom] = useState<FetchedDomContent | null>(null);
  const [domIframe, setDomIframe] = useState<FetchedDomContent | null>(null);

  function sendMessageToParent(data: ExtensionMessage) {
    window.parent.postMessage(data, '*');
  }

  async function refetchDom(
    timeoutMs: number,
    options: RefetchDomOptions = {}
  ): Promise<FetchedDomContent | null> {
    const { approvedHostname, url, useNoModify } = options;
    if (url) {
      sendMessageToParent({
        type: ChromeExtensionMessageType.FETCH_OTHER_PAGE,
        payload: url,
      });
    } else {
      if (useNoModify) {
        sendMessageToParent({
          type: ChromeExtensionMessageType.FETCH_DOM_IFRAME_CONTENT,
        });
      } else {
        sendMessageToParent({
          type: ChromeExtensionMessageType.FETCH_DOM_CONTENT,
          approvedHostname,
        });
      }
    }

    return new Promise<FetchedDomContent | null>((resolve, reject) => {
      const timeout = setTimeout(() => {
        window.removeEventListener('message', handleResponseMessage);
        reject(new Error('Timeout waiting for refetched DOM content'));
      }, timeoutMs);

      function settle(result: FetchedDomContent | null) {
        window.removeEventListener('message', handleResponseMessage);
        clearTimeout(timeout);
        resolve(result);
      }

      function handleResponseMessage(event: { data: ExtensionMessage }) {
        if (event.data.type === ChromeExtensionMessageType.UNSUPPORTED_URL) {
          settle(null);
          return;
        }
        const isIframeContent =
          event.data.type ===
          ChromeExtensionMessageType.SEND_DOM_IFRAME_CONTENT;
        const isDomContent =
          event.data.type === ChromeExtensionMessageType.SEND_DOM_CONTENT;
        if (
          (useNoModify && !isIframeContent) ||
          (!useNoModify && !isDomContent)
        ) {
          return;
        }

        settle({
          domContent: event.data.payload ?? '',
          timeReceived: new Date(),
        });
      }

      window.addEventListener('message', handleResponseMessage);
    });
  }

  // TODO(mike): Delete with the DAT expanded-DOM capture in chrome; nothing
  // calls this since the DAT equipment page was replaced by Vee chat.
  async function refetchDatEquipmentExpandedDom(
    timeoutMs: number
  ): Promise<FetchedDomContent> {
    sendMessageToParent({
      type: ChromeExtensionMessageType.FETCH_DAT_EQUIPMENT_EXPANDED_DOM,
    });

    return new Promise<FetchedDomContent>((resolve, reject) => {
      const timeout = setTimeout(() => {
        window.removeEventListener('message', handleResponseMessage);
        reject(new Error('Timeout waiting for expanded DAT equipment DOM'));
      }, timeoutMs);

      function handleResponseMessage(event: { data: ExtensionMessage }) {
        if (event.data.type === ChromeExtensionMessageType.UNSUPPORTED_URL) {
          window.removeEventListener('message', handleResponseMessage);
          clearTimeout(timeout);
          reject(
            new Error(
              'Active tab is not on DAT equipment search — open results there first'
            )
          );
          return;
        }
        if (
          event.data.type !==
          ChromeExtensionMessageType.SEND_DAT_EQUIPMENT_EXPANDED_DOM
        ) {
          return;
        }

        const newDom = {
          domContent: event.data.payload ?? '',
          timeReceived: new Date(),
        };
        window.removeEventListener('message', handleResponseMessage);
        resolve(newDom);
        clearTimeout(timeout);
      }

      window.addEventListener('message', handleResponseMessage);
    });
  }

  useEffect(() => {
    const handleMessage = (event: {
      data: ExtensionMessage;
      origin: string;
    }) => {
      console.log('Sidekick received message:', event);
      switch (event.data.type) {
        case ChromeExtensionMessageType.URL_UPDATED: {
          console.log('URL_UPDATED', event.data.payload);
          setParentUrl(event.data.payload ?? '');
          sendMessageToParent({
            type: ChromeExtensionMessageType.FETCH_DOM_CONTENT,
          });
          sendMessageToParent({
            type: ChromeExtensionMessageType.FETCH_DOM_IFRAME_CONTENT,
          });
          break;
        }
        case ChromeExtensionMessageType.SEND_DOM_CONTENT: {
          const now = new Date();
          setDom({
            domContent: event.data.payload ?? '',
            timeReceived: now,
          });
          break;
        }
        case ChromeExtensionMessageType.SEND_DOM_IFRAME_CONTENT: {
          const now = new Date();
          setDomIframe({
            domContent: event.data.payload ?? '',
            timeReceived: now,
          });
          break;
        }
        case ChromeExtensionMessageType.UNSUPPORTED_URL: {
          console.log(
            'URL not supported by Vooma sidekick',
            event.data.payload
          );
          break;
        }
      }
    };

    window.addEventListener('message', handleMessage);

    sendMessageToParent({
      type: ChromeExtensionMessageType.IFRAME_READY,
      payload: 'Ready from iframe',
    });

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  return (
    <ExtensionMessagingContext.Provider
      value={{
        sendMessageToParent,
        parentUrl,
        domIframe,
        dom,
        refetchDom,
        refetchDatEquipmentExpandedDom,
      }}
    >
      {children}
    </ExtensionMessagingContext.Provider>
  );
};
